import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { ArrowClockwise, FileText, Save } from "react-bootstrap-icons";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";

const CONTENT_PAGES = [
  {
    key: "about",
    label: "About Us",
    endpoint: "about-content",
    placeholder: "Write the About Us content here...",
  },
  {
    key: "terms",
    label: "Terms & Conditions",
    endpoint: "terms-content",
    placeholder: "Write the Terms & Conditions content here...",
  },
  {
    key: "policy",
    label: "Privacy Policy",
    endpoint: "policy-content",
    placeholder: "Write the Privacy Policy content here...",
  },
];

const getContentValue = (payload) => {
  if (typeof payload === "string") return payload;
  return (
    payload?.content ||
    payload?.data?.content ||
    payload?.data?.page?.content ||
    payload?.page?.content ||
    ""
  );
};

const StaticContent = () => {
  const [activeKey, setActiveKey] = useState(CONTENT_PAGES[0].key);
  const [contentByKey, setContentByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  const activePage = useMemo(
    () => CONTENT_PAGES.find((page) => page.key === activeKey),
    [activeKey]
  );

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError("");

      const responses = await Promise.all(
        CONTENT_PAGES.map((page) =>
          axios.get(`${API_BASE_URL}/api/admin/content/${page.endpoint}`)
        )
      );

      const nextContent = {};
      CONTENT_PAGES.forEach((page, index) => {
        nextContent[page.key] = getContentValue(responses[index].data);
      });
      setContentByKey(nextContent);
    } catch (err) {
      console.error("Fetch static content error:", err);
      setError("Failed to load static content");
      toast.error("Failed to load static content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleContentChange = (value) => {
    setContentByKey((prev) => ({
      ...prev,
      [activeKey]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!activePage) return;

    try {
      setSaving(true);
      await axios.post(
        `${API_BASE_URL}/api/admin/content/${activePage.endpoint}`,
        { content: contentByKey[activeKey] || "" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(`${activePage.label} content saved`);
    } catch (err) {
      console.error("Save static content error:", err);
      toast.error(`Failed to save ${activePage.label} content`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3 justify-content-between">
            <Col md={7}>
              <h4 className="fw-bold mb-0">Static Content</h4>
              <small className="text-muted">
                Manage public About, Terms, and Privacy page content
              </small>
            </Col>
            <Col md={2} className="text-md-end">
              <Button
                variant="outline-primary"
                onClick={fetchContent}
                disabled={loading || saving}
                title="Refresh"
              >
                <ArrowClockwise className="me-2" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-4">
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-2">
              {CONTENT_PAGES.map((page) => (
                <Button
                  key={page.key}
                  variant={activeKey === page.key ? "primary" : "light"}
                  className="w-100 text-start mb-2 d-flex align-items-center"
                  onClick={() => setActiveKey(page.key)}
                >
                  <FileText className="me-2" />
                  {page.label}
                </Button>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted">Loading content...</p>
                </div>
              ) : (
                <Form onSubmit={handleSave}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      {activePage.label}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={16}
                      value={contentByKey[activeKey] || ""}
                      onChange={(event) =>
                        handleContentChange(event.target.value)
                      }
                      placeholder={activePage.placeholder}
                      disabled={saving}
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-2"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="me-2" />
                          Save Content
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StaticContent;
